import React, { useState } from "react";
import { Modal, Upload } from "antd";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";
import useAxiosInstance from "../../lib/useAxiosInstance";

const { Dragger } = Upload;

const YojnaTrackerBulkUpload = ({ onClose }) => {
  const axiosInstance = useAxiosInstance();
  const queryClient = useQueryClient();

  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleDownloadTemplate = async () => {
    try {
      const response = await axiosInstance.get("api/yojna-tracker/template", {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "yojna-tracker-template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Could not download the template. Please try again.");
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file first.");
      return;
    }
    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);
    setResult(null);
    try {
      const response = await axiosInstance.post("api/yojna-tracker/bulk-upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const data = response?.data?.data;
      setResult(data);
      toast.success(response?.data?.message || "Schemes imported successfully");
      queryClient.invalidateQueries("yojnaTrackerSchemes");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Upload failed. Please check the file and try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal open onCancel={onClose} footer={null} width={640} className="yt-form-modal" closeIcon={null}>
      <div className="yt-modal-head yt-form-head">
        <div className="yt-modal-head-actions">
          <button className="yt-icon-btn" title="Close" onClick={onClose}>✕</button>
        </div>
        <span className="yt-cat-tag" style={{ background: "#556070" }}>Bulk import</span>
        <h2>Upload schemes from Excel</h2>
      </div>

      <div className="yt-bulk-body">
        <div className="yt-bulk-help">
          <p className="yt-section-label">Expected format</p>
          <p>
            The workbook should have a <strong>Schemes</strong> sheet with the columns:
            <em> Scheme, Launch Date, Scheme Overview, Achievements (All over India),
            Achievements - Uttar Pradesh, Additional</em>. An optional <strong>Sources</strong> sheet
            (<em>Scheme, Source Label, URL</em>) adds the related links for each scheme, matched by scheme name.
          </p>
          <p className="yt-field-hint">
            Achievements are split into bullet points one per line. A scheme already in the tracker is
            updated rather than duplicated. Categories are auto-tagged when the Category column is blank.
          </p>
          <button type="button" className="yt-link-btn" onClick={handleDownloadTemplate}>
            ⬇ Download sample template
          </button>
        </div>

        <Dragger
          name="file"
          multiple={false}
          maxCount={1}
          accept=".xlsx,.xls,.csv"
          beforeUpload={(selected) => {
            setFile(selected);
            setResult(null);
            return false;
          }}
          onRemove={() => {
            setFile(null);
            setResult(null);
          }}
          className="yt-dragger"
        >
          <p className="yt-dragger-icon">📄</p>
          <p className="yt-dragger-text">Click or drag an Excel file to this area</p>
          <p className="yt-dragger-hint">Supports a single .xlsx, .xls or .csv file (max 20 MB)</p>
        </Dragger>

        {result && (
          <div className="yt-import-result">
            <div className="yt-result-stats">
              <div className="yt-result-stat">
                <span className="yt-result-num yt-added">{result.created}</span>
                <span className="yt-result-lbl">Added</span>
              </div>
              <div className="yt-result-stat">
                <span className="yt-result-num yt-updated">{result.updated}</span>
                <span className="yt-result-lbl">Updated</span>
              </div>
              <div className="yt-result-stat">
                <span className="yt-result-num yt-skipped">{result.failed}</span>
                <span className="yt-result-lbl">Skipped</span>
              </div>
            </div>
            {result.errors?.length > 0 && (
              <ul className="yt-result-errors">
                {result.errors.map((e, i) => (
                  <li key={i}>Row {e.row}: {e.message}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="yt-form-actions">
          <button type="button" className="yt-btn yt-btn-primary" onClick={handleUpload} disabled={uploading || !file}>
            {uploading ? "Importing..." : "Import schemes"}
          </button>
          <button type="button" className="yt-btn yt-btn-cancel" onClick={onClose}>
            {result ? "Done" : "Cancel"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default YojnaTrackerBulkUpload;
